import { hasAnyScope } from '../permissions/scopePermissions';

/**
 * APP_NAV_ITEMS — single source of truth for navigation AND route generation.
 *
 * Drives:
 *   1. Sidebar (grouped + scope-filtered via getAccessibleNavGroups)
 *   2. React Router dynamic routes in routes/index.jsx (flat APP_NAV_ITEMS)
 *   3. First-accessible-route redirect (getAccessibleNavItems)
 *
 * Grouping: set `group` (+ optional `groupIconKey`) on items that belong
 * under a collapsible parent menu (e.g. "System Administrator"). Items with
 * no `group` render as top-level links. Order within this array is preserved
 * in the sidebar; the first item of a group defines the group's position.
 *
 * When adding a module:
 *   1. Add entry here (with `group` if it belongs under a parent)
 *   2. Add scope aliases in src/permissions/scopePermissions.js
 *   3. Add renderModuleRoute() case in routes/index.jsx
 */
export const APP_NAV_ITEMS = [
  {
    path:           '/dashboard',
    label:          'Dashboard',
    iconKey:        'dashboard',
    eyebrow:        'CRM Suite Dashboard',
    description:    'High-level summary and key metrics.',
    requiredScopes: ['dashboard.view'],
  },
  {
    // Placed directly under Dashboard because it is the first thing an agent
    // needs each morning — "what do I owe today?" outranks every record list.
    path:           '/my-day',
    label:          'My Day',
    iconKey:        'myDay',
    eyebrow:        'CRM',
    description:    'Your own overdue, due-today and upcoming work — in one request. Tick things off from here.',
    requiredScopes: ['activities.view'],
  },
  // ── CRM core. Top-level on purpose: these are the two records an agent opens
  //    dozens of times a day, and burying a daily destination behind a
  //    collapsible group is a tax on every single visit (Rule 56 Adoption-First).
  {
    path:           '/organizations',
    label:          'Organizations',
    iconKey:        'organizations',
    eyebrow:        'CRM',
    description:    'Customer companies (accounts) — the records contacts, deals and tickets hang off.',
    requiredScopes: ['organizations.view'],
  },
  {
    path:           '/leads',
    label:          'Leads',
    iconKey:        'leads',
    eyebrow:        'CRM',
    description:    'Raw, unqualified interest. The lead survives conversion forever as the attribution record.',
    requiredScopes: ['leads.view'],
  },
  {
    // The board a sales manager opens first — top-level, above the record lists.
    path:           '/pipeline',
    label:          'Pipeline',
    iconKey:        'pipeline',
    eyebrow:        'CRM',
    description:    'The Kanban board. Deals grouped by stage, with the value and weighted forecast in each column.',
    requiredScopes: ['opportunities.view'],
  },
  {
    path:           '/opportunities',
    label:          'Opportunities',
    iconKey:        'opportunities',
    eyebrow:        'CRM',
    description:    'Every deal as a sortable, filterable table — the view the board cannot give you.',
    requiredScopes: ['opportunities.view'],
  },
  {
    path:           '/contacts',
    label:          'Contacts',
    iconKey:        'contacts',
    eyebrow:        'CRM',
    description:    'The people. A contact with no company is valid — not every customer is a business.',
    requiredScopes: ['contacts.view'],
  },
  {
    path:           '/activities',
    label:          'Activities',
    iconKey:        'activities',
    eyebrow:        'CRM',
    description:    'The whole log — notes, calls, meetings and follow-ups — filterable across the team, which My Day deliberately is not.',
    requiredScopes: ['activities.view'],
  },
  {
    path:           '/admin/users',
    label:          'OAuth Users',
    iconKey:        'oauthUsers',
    eyebrow:        'Admin',
    description:    'Create, edit, and assign roles + scopes to OAuth users.',
    requiredScopes: ['oauth-users.view'],
    group:          'System Administrator',
    groupIconKey:   'admin',
  },
  {
    path:           '/admin/oauth-clients',
    label:          'OAuth Clients',
    iconKey:        'oauthClients',
    eyebrow:        'Admin',
    description:    'Register, rotate secrets, and manage OAuth client credentials.',
    requiredScopes: ['oauth-clients.view'],
    group:          'System Administrator',
    groupIconKey:   'admin',
  },
  {
    path:           '/admin/upload-s3-public-share-tokens',
    label:          'Public Share Tokens',
    iconKey:        'shareToken',
    eyebrow:        'Admin',
    description:    'Permanent public share URLs for S3-uploaded files. Manage, regenerate, and revoke.',
    requiredScopes: ['upload-s3-public-share-token.view'],
    group:          'System Administrator',
    groupIconKey:   'admin',
  },
  {
    path:           '/example-batch-upload',
    label:          'Example Batch Upload',
    iconKey:        'batchUpload',
    eyebrow:        'Template',
    description:    'Reference CSV/XLSX import. Daredaretyo per-row processing — copy this module for your own batch imports.',
    requiredScopes: ['example-batch-upload.view'],
    group:          'Tools',
    groupIconKey:   'tools',
  },
  {
    // A supporting catalog, not a daily destination — grouped rather than
    // top-level so the sidebar's first screen stays the records agents live in.
    path:           '/tags',
    label:          'Tags',
    iconKey:        'tags',
    eyebrow:        'CRM',
    description:    'Company-wide labels. Renaming one updates every record it is pinned to.',
    requiredScopes: ['tags.view'],
    group:          'Tools',
    groupIconKey:   'tools',
  },
  {
    path:           '/sales-teams',
    label:          'Sales Teams',
    iconKey:        'salesTeams',
    eyebrow:        'CRM Admin',
    description:    'Teams and membership. Membership decides whose records each person can see (Rule 65 team tier).',
    requiredScopes: ['sales-teams.view'],
    group:          'CRM Settings',
    groupIconKey:   'assignmentRules',
  },
  {
    // Rule 75 — stages are DATA, so editing them belongs in settings, not in a
    // migration. This page is what makes that claim true for an admin.
    path:           '/pipeline-settings',
    label:          'Pipeline Stages',
    iconKey:        'pipeline',
    eyebrow:        'CRM Admin',
    description:    'Stage names, order, probability and win/lose type. Probability drives the weighted forecast; won/lost stages are terminal and demand an amount or a reason.',
    requiredScopes: ['pipelines.view'],
    group:          'CRM Settings',
    groupIconKey:   'assignmentRules',
  },
  {
    path:           '/assignment-rules',
    label:          'Assignment Rules',
    iconKey:        'assignmentRules',
    eyebrow:        'CRM Admin',
    description:    'Lead routing. Evaluated by priority — the first match assigns the lead and evaluation stops.',
    requiredScopes: ['sales-teams.view'],
    group:          'CRM Settings',
    groupIconKey:   'assignmentRules',
  },
  {
    // Top-level and above internal Chat: a waiting website visitor is a customer
    // with a stopwatch running, and internal chat is not.
    path:           '/livechat',
    label:          'Live Chat',
    iconKey:        'livechat',
    eyebrow:        'Realtime',
    description:    'The website widget queue. Claim a waiting visitor, reply, leave internal notes, and convert the conversation into a lead.',
    requiredScopes: ['livechat.view'],
  },
  {
    path:           '/chat',
    label:          'Chat',
    iconKey:        'chat',
    eyebrow:        'Realtime',
    description:    '1-to-1 and group conversations with live message delivery via WebSocket. Includes voice + video calls (Rule 74) accessible from the thread header.',
    requiredScopes: ['chat.view'],
  },
  // Architecture Rule 74 — Voice + Video Calls deliberately have NO
  // top-level nav entry. The in-call page (/call/:callId) is reached by
  // starting/accepting a call; per-thread history (/threads/:threadId/calls)
  // is reached from the chat thread header. Both are registered as standalone
  // routes in src/routes/index.jsx, not iterated from this nav list.

  {
    path:           '/admin/audit-logs',
    label:          'Audit Logs',
    iconKey:        'auditLogs',
    eyebrow:        'Admin',
    description:    'System-wide activity trail — who did what, when, on which resource. Read-only.',
    requiredScopes: ['admin-audit-logs.view'],
    group:          'System Administrator',
    groupIconKey:   'admin',
  },
  {
    path:           '/admin/outbound-http-logs',
    label:          'Outbound HTTP Logs',
    iconKey:        'outboundHttp',
    eyebrow:        'Admin',
    description:    'Every outbound HTTP call the app made — request/response samples, latency. Read-only.',
    requiredScopes: ['admin-outbound-http-logs.view'],
    group:          'System Administrator',
    groupIconKey:   'admin',
  },
  {
    path:           '/admin/field-history',
    label:          'Field History',
    iconKey:        'fieldHistory',
    eyebrow:        'Admin',
    description:    'Per-column change timeline across every table — old → new value. Read-only.',
    requiredScopes: ['admin-field-history.view'],
    group:          'System Administrator',
    groupIconKey:   'admin',
  },
  {
    path:           '/admin/login-attempts',
    label:          'Login Attempts',
    iconKey:        'loginAttempts',
    eyebrow:        'Admin',
    description:    'Every authentication attempt and lockout signal. Read-only.',
    requiredScopes: ['admin-login-attempts.view'],
    group:          'System Administrator',
    groupIconKey:   'admin',
  },
  {
    path:           '/admin/chat-messages',
    label:          'Chat Messages',
    iconKey:        'chatMessages',
    eyebrow:        'Admin',
    description:    'All chat messages across every thread, including deleted, for moderation. Read-only.',
    requiredScopes: ['admin-chat-messages.view'],
    group:          'System Administrator',
    groupIconKey:   'admin',
  },
  {
    path:           '/admin/webhook-deliveries',
    label:          'Webhook Deliveries',
    iconKey:        'webhookDeliveries',
    eyebrow:        'Admin',
    description:    'Every outbound webhook delivery attempt across all subscriptions. Read-only.',
    requiredScopes: ['admin-webhook-deliveries.view'],
    group:          'System Administrator',
    groupIconKey:   'admin',
  },
  {
    path:           '/admin/failed-jobs',
    label:          'Failed Jobs',
    iconKey:        'failedJobs',
    eyebrow:        'Admin',
    description:    'Dead-letter queue — jobs that exhausted retries (Rule 32). Re-queue with one click.',
    requiredScopes: ['admin-failed-jobs.view'],
    group:          'System Administrator',
    groupIconKey:   'admin',
  },
  {
    path:           '/admin/system-settings',
    label:          'System Settings',
    iconKey:        'tools',
    eyebrow:        'Admin',
    description:    'Runtime-tunable knobs (Rule 74 Phase 1). Edit max group call participants, ringing TTL, call token TTL, and more — changes apply within milliseconds. Direct-call cap stays immutable.',
    requiredScopes: ['system-settings.view'],
    group:          'System Administrator',
    groupIconKey:   'admin',
  },

  // Add new module nav items here. Set `group` + `groupIconKey` to nest it
  // under a collapsible parent (reuse an existing group label to extend it):
  // {
  //   path: '/{route-path}', label: '{Module Label}', iconKey: '{camelCaseIconKey}',
  //   eyebrow: '{Module Label}', description: '{One sentence.}',
  //   requiredScopes: ['{module-key}.view'],
  //   group: 'System Administrator', groupIconKey: 'admin',
  // },
];

/**
 * True if the user holds any scope required by this nav item.
 * Special case: /dashboard is accessible with zero scopes.
 */
export function canAccessNavItem(userScopes, navItem) {
  const normalized = Array.isArray(userScopes) ? userScopes : [];
  if (navItem?.path === '/dashboard' && normalized.length === 0) return true;
  return hasAnyScope(normalized, navItem?.requiredScopes ?? []);
}

/**
 * Flat list of accessible items (used for the fallback-route redirect).
 * Falls back to dashboard if nothing matches.
 */
export function getAccessibleNavItems(userScopes) {
  const items = APP_NAV_ITEMS.filter((item) => canAccessNavItem(userScopes, item));
  return items.length > 0 ? items : APP_NAV_ITEMS.filter((i) => i.path === '/dashboard');
}

/**
 * Sidebar structure: an ordered list of entries that are either
 *   { type: 'item',  item }
 *   { type: 'group', label, iconKey, children: [item, ...] }
 *
 * Groups appear at the position of their first accessible child. A group
 * with no accessible children is omitted entirely.
 */
export function getAccessibleNavGroups(userScopes) {
  const accessible = getAccessibleNavItems(userScopes);
  const entries = [];
  const groupIndex = new Map();

  accessible.forEach((item) => {
    if (!item.group) {
      entries.push({ type: 'item', item });
      return;
    }
    if (!groupIndex.has(item.group)) {
      const group = {
        type:     'group',
        label:    item.group,
        iconKey:  item.groupIconKey ?? 'folder',
        children: [],
      };
      groupIndex.set(item.group, group);
      entries.push(group);
    }
    groupIndex.get(item.group).children.push(item);
  });

  return entries;
}
